import React, { useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  FaReact, FaPython, FaJsSquare, 
  FaGitAlt, FaAws, FaDocker, FaLinux, FaDatabase, FaGithub
} from 'react-icons/fa';
import { 
  SiTypescript, SiDjango, SiMysql, SiSqlite,
  SiTailwindcss, SiGoogleappsscript, SiOpenai, SiFastapi,
  SiSelenium, SiQt, SiHive, SiTensorflow, SiPytorch, SiGithubactions, SiFlask
} from 'react-icons/si';

const Section = styled.section`
  padding: 8rem 0;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 0 1.25rem;
  }
`;

const SectionHeader = styled(motion.div)`
  margin-bottom: 4rem;
  
  h2 {
    font-size: 2.5rem;
    color: ${props => props.theme.colors.text};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    &::before {
      content: '02.';
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.25rem;
      color: ${props => props.theme.colors.primary};
    }
    
    &::after {
      content: '';
      flex: 1;
      height: 1px;
      background: ${props => props.theme.colors.border};
      margin-left: 2rem;
    }
  }
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
`;

const pulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.4); }
  50% { box-shadow: 0 0 0 4px rgba(52, 211, 153, 0); }
`;

const SkillCard = styled(motion.div)`
  background: ${props => props.theme.colors.surface};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-4px);
    box-shadow: 
      0 10px 40px -10px rgba(0, 0, 0, 0.5),
      0 0 20px rgba(56, 189, 248, 0.1);
  }
`;

const CardHeader = styled.div`
  background: ${props => props.theme.colors.surfaceLight};
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .title-area {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  
  .dots {
    display: flex;
    gap: 6px;
    
    span {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      
      &:nth-child(1) { background: #FF5F56; }
      &:nth-child(2) { background: #FFBD2E; }
      &:nth-child(3) { background: #27CA40; }
    }
  }
  
  h3 {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.9rem;
    color: ${props => props.theme.colors.textDim};
    font-weight: 500;
  }
  
  .status {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.theme.colors.success};
    animation: ${pulse} 2s infinite;
  }
`;

const CardBody = styled.div`
  padding: 1.5rem;
`;

const SkillList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const fillBar = keyframes`
  from { width: 0%; }
  to { width: var(--target-width); }
`;

const SkillItem = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.textDim};
  font-size: 0.9rem;
  
  .icon {
    font-size: 1.25rem;
    color: ${props => props.theme.colors.text};
    width: 28px;
    display: flex;
    justify-content: center;
    transition: all 0.3s;
  }
  
  &:hover .icon {
    color: ${props => props.theme.colors.primary};
    transform: scale(1.2);
  }
  
  .name {
    flex: 1;
    font-weight: 500;
  }
  
  .level {
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.75rem;
    color: ${props => props.theme.colors.primary};
    min-width: 40px;
    text-align: right;
  }
`;

const BarContainer = styled.div`
  width: 100px;
  height: 6px;
  background: ${props => props.theme.colors.border};
  border-radius: 3px;
  overflow: hidden;
  position: relative;
`;

const Bar = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.accent});
  border-radius: 3px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    animation: shimmer 2s infinite;
  }
  
  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }
`;

const SkillItemComponent = ({ skill, isInView, delay }) => {
  return (
    <SkillItem>
      <span className="icon">{skill.icon}</span>
      <span className="name">{skill.name}</span>
      <span className="level">{skill.level}</span>
      <BarContainer>
        <Bar 
          initial={{ width: 0 }}
          animate={isInView ? { width: skill.level } : { width: 0 }}
          transition={{ 
            duration: 1, 
            delay: delay * 0.15,
            ease: [0.4, 0, 0.2, 1]
          }}
        />
      </BarContainer>
    </SkillItem>
  );
};

const SkillCardComponent = ({ category, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <SkillCard
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <CardHeader>
        <div className="title-area">
          <div className="dots">
            <span /><span /><span />
          </div>
          <h3>{category.title}</h3>
        </div>
        <div className="status" />
      </CardHeader>
      <CardBody>
        <SkillList>
          {category.skills.map((skill, idx) => (
            <SkillItemComponent 
              key={idx} 
              skill={skill} 
              isInView={isInView}
              delay={idx}
            />
          ))}
        </SkillList>
      </CardBody>
    </SkillCard>
  );
};

const Skills = () => {
  const categories = [
    {
      title: "frontend.tsx",
      skills: [
        { name: "React", icon: <FaReact />, level: "90%" },
        { name: "JavaScript", icon: <FaJsSquare />, level: "80%" },
        { name: "TypeScript", icon: <SiTypescript />, level: "70%" },
        { name: "Tailwind CSS", icon: <SiTailwindcss />, level: "70%" },

      ]
    },
    {
      title: "backend.py",
      skills: [
        { name: "Python", icon: <FaPython />, level: "95%" },
        { name: "FastAPI", icon: <SiFastapi />, level: "90%" },
        { name: "Flask", icon: <SiFlask />, level: "85%" },
        { name: "Django", icon: <SiDjango />, level: "80%" },
      ]
    },
    {
      title: "database.sql",
      skills: [
        { name: "Hive", icon: <SiHive />, level: "90%" },
        { name: "MySQL", icon: <SiMysql />, level: "85%" },
        { name: "SQLite", icon: <SiSqlite />, level: "85%" },
        { name: "DB Design", icon: <FaDatabase />, level: "80%" },
      ]
    },
    {
      title: "automation.sh",
      skills: [
        { name: "Selenium", icon: <SiSelenium />, level: "95%" },
        { name: "Playwright", icon: <FaJsSquare />, level: "90%" },
        { name: "Apps Script", icon: <SiGoogleappsscript />, level: "90%" },
        { name: "PyQT", icon: <SiQt />, level: "80%" },
      ]
    },
    {
      title: "devops.yml",
      skills: [
        { name: "Git", icon: <FaGithub />, level: "95%" },
        { name: "CI/CD", icon: <SiGithubactions />, level: "90%" },
        { name: "Linux", icon: <FaLinux />, level: "90%" },
        { name: "Docker", icon: <FaDocker />, level: "80%" },
        { name: "AWS", icon: <FaAws />, level: "70%" },
      ]
    },
    {
      title: "ai_tools.json",
      skills: [
        { name: "AI Prompt Engineering", icon: <SiOpenai />, level: "95%" },
        { name: "Machine Learning", icon: <SiTensorflow />, level: "80%" },
        { name: "Deep Learning", icon: <SiPytorch />, level: "80%" },
      ]
    }
  ];

  return (
    <Section id="skills">
      <Container>
        <SectionHeader
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2>Tech Stack</h2>
        </SectionHeader>
        
        <CategoryGrid>
          {categories.map((category, index) => (
            <SkillCardComponent 
              key={index}
              category={category}
              index={index}
            />
          ))}
        </CategoryGrid>
      </Container>
    </Section>
  );
};

export default Skills;
